import { AVAILABLE_SERVICE, CONSULTATION_LANDING_STEP, CONSULTATION_TYPE } from '@abiglobalhealth/go-types'
import { useContext } from 'react'

import { getInitialModule } from '@/api/modules'
import { ConsultationContext, ConsultationContextProps } from '@/contexts/consultation'
import { useModule } from '@/hooks/useModule'
import { useQuestion } from '@/hooks/useQuestion'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { getInitialStep } from '@/utils/step'
import { formatTrace } from '@/utils/traces'

interface UseConsultationTypeProps {
  consultationType: ConsultationContextProps['consultationType']
  updateConsultationType: (consultationType: AVAILABLE_SERVICE) => void
  updatingConsultationType: ConsultationContextProps['updatingConsultationType']
  setConsultationType: ConsultationContextProps['setConsultationType']
}

const updateQueryParam = (consultationType: AVAILABLE_SERVICE) => {
  const url = new URL(window.location.href)
  url.searchParams.set('q', consultationType)
  window.history.replaceState(null, '', url.toString())
}

export const useConsultationType = (): UseConsultationTypeProps => {
  const { consultationType, setConsultationType, updatingConsultationType, setUpdatingConsultationType, checkLimitExceeded } =
    useContext(ConsultationContext)
  const { updateModule } = useModule()
  const { setTraces } = useTraces()
  const { user } = useUser()
  const { updateStep } = useStep()
  const { resetQuestion } = useQuestion()

  const updateConsultationType = (consultationType: AVAILABLE_SERVICE) => {
    setUpdatingConsultationType(true)
    getInitialModule({ serviceType: consultationType, uniqueId: user?.data.uniqueId!, userId: user?.data?.userId! })
      .then(async ({ module }) => {
        updateModule(module)

        // Update queryParam q to match the consultation type
        updateQueryParam(consultationType)

        // If the module has steps, update the current step and traces
        if ('steps' in module && 'initialSteps' in module && module.initialSteps.length) {
          const currentStep = getInitialStep({ module, user: user! })
          updateStep(currentStep)
          setTraces([formatTrace(module.id, currentStep.id)])
          return
        }

        setTraces([formatTrace(module.id, CONSULTATION_LANDING_STEP.id)])
        resetQuestion()
        await checkLimitExceeded(consultationType as CONSULTATION_TYPE)
      })
      .finally(() => {
        setUpdatingConsultationType(false)
      })
  }

  return {
    consultationType,
    updateConsultationType,
    updatingConsultationType,
    setConsultationType
  }
}
